import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
    Box, 
    Button, 
    TextField, 
    Select, 
    MenuItem, 
    FormControl,
    InputLabel,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Popover
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    FileDownload as FileDownloadIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { Checkbox, ListItemText, TableSortLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import { customSearch, exportCustomSearch } from '../../../services/reportingService';
import { getAllProjectsNoPagination, getAssignedProjects } from '../../../services/projectService';
import { getAllUsersNoPagination } from '../../../services/userService';

// Function to validate date format (MM/DD/YYYY)
const isValidDateFormat = (dateString) => {
    if (!dateString) return true; // Empty is valid (will be caught by required validation)
    
    // Check format using regex (MM/DD/YYYY)
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!regex.test(dateString)) return false;
    
    // Parse the date parts
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    
    // Create a date object and check if it's valid
    const date = new Date(year, month - 1, day);
    return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
    );
};

const DateInput = ({ label, value, onChange, onCalendarClick, error }) => {
    const handleChange = (e) => {
        const newValue = e.target.value;
        onChange(newValue);
    };
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
                label={label}
                value={value}
                onChange={handleChange}
                size="small"
                sx={{ width: 140 }}
                placeholder="MM/DD/YYYY"
                error={error && value && !isValidDateFormat(value)}
                helperText={error && value && !isValidDateFormat(value) ? "Invalid format" : ""}
                InputProps={{
                    endAdornment: (
                        <IconButton 
                            size="small" 
                            onClick={onCalendarClick}
                            sx={{ mr: -1 }}
                        >
                            <CalendarIcon fontSize="small" />
                        </IconButton>
                    )
                }}
            />
        </Box>
    );
};

const CustomSearch = ({ onBack }) => {
    // Calendar state
    const [anchorEl, setAnchorEl] = useState(null);
    const [calendarType, setCalendarType] = useState(null); // 'start' or 'end'
    // Date state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateError, setDateError] = useState('');

    // Filter state
    const [projectIds, setProjectIds] = useState([]); // Changed to array for multiple selection
    const [userId, setUserId] = useState('');
    const [status, setStatus] = useState('');
    
    // Data state
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');

    // Sorting state
    const [sortColumn, setSortColumn] = useState('status'); // 'username', 'user', 'project', 'status'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [sortedResults, setSortedResults] = useState([]);

    const { user } = useAuth(); // Get the current user
    
    // Additional state for teams
    const [teams, setTeams] = useState([]);
    const [allProjectsUnfiltered, setAllProjectsUnfiltered] = useState([]);

    // Format date range from YYYY-MM-DD to MM/DD/YY format
    const formatDateRange = (dateRangeString) => {
        if (!dateRangeString) return '';
        
        // Split the date range string (e.g., "2025-05-04 to 2025-05-10")
        const parts = dateRangeString.split(' to ');
        if (parts.length !== 2) return dateRangeString; // Return original if format is unexpected
        
        const formatSingleDate = (dateStr) => {
            const [year, month, day] = dateStr.split('-');
            const shortYear = year.slice(-2); // Get last 2 digits of year
            return `${month}/${day}/${shortYear}`;
        };
        
        const startFormatted = formatSingleDate(parts[0]);
        const endFormatted = formatSingleDate(parts[1]);
        
        return `${startFormatted} - ${endFormatted}`;
    };

    // Define status priority order for sorting
    const getStatusPriority = (status) => {
        const statusOrder = {
            'Approved': 1,
            'Certified': 2,
            'Partially Approved': 3,
            'Partially Certified': 4,
            'Uncertified': 5,
            'Timecard Not Created': 6
        };
        return statusOrder[status] || 999;
    };

    // Generic sort function for all columns
    const sortResults = (resultsToSort, column, order) => {
        const dataRows = resultsToSort.filter(row => row.username); // Only data rows, not total row
        
        const sortedDataRows = [...dataRows].sort((a, b) => {
            let valueA, valueB;
            
            switch (column) {
                case 'username':
                    valueA = (a.username || '').toLowerCase();
                    valueB = (b.username || '').toLowerCase();
                    break;
                case 'user':
                    valueA = (a.user || '').toLowerCase();
                    valueB = (b.user || '').toLowerCase();
                    break;
                case 'project':
                    valueA = (a.project || '').toLowerCase();
                    valueB = (b.project || '').toLowerCase();
                    break;
                case 'status':
                    valueA = getStatusPriority(a.status);
                    valueB = getStatusPriority(b.status);
                    break;
                default:
                    return 0;
            }
            
            if (column === 'status') {
                // For status, use numeric comparison
                if (order === 'asc') {
                    return valueA - valueB;
                } else {
                    return valueB - valueA;
                }
            } else {
                // For text columns, use string comparison
                if (order === 'asc') {
                    return valueA.localeCompare(valueB);
                } else {
                    return valueB.localeCompare(valueA);
                }
            }
        });
        
        return sortedDataRows;
    };

    // Handle column sort
    const handleColumnSort = (column) => {
        const newOrder = (sortColumn === column && sortOrder === 'asc') ? 'desc' : 'asc';
        setSortColumn(column);
        setSortOrder(newOrder);
        const sorted = sortResults(results, column, newOrder);
        setSortedResults(sorted);
    };

    // Update sorted results when results change
    useEffect(() => {
        if (results.length > 0) {
            const sorted = sortResults(results, sortColumn, sortOrder);
            setSortedResults(sorted);
        } else {
            setSortedResults([]);
        }
    }, [results, sortColumn, sortOrder]);

    // Load projects, users, and teams
    useEffect(() => {
        const loadData = async () => {
            try {
                // For regular users, use assigned projects API; for others, use all projects
                const projectsPromise = user && user.role === 'user' 
                    ? getAssignedProjects()
                    : getAllProjectsNoPagination();
                
                // For regular users, we don't need to fetch all users since they can only see themselves
                const usersPromise = user && user.role === 'user'
                    ? Promise.resolve([]) // Empty array since we'll set it manually
                    : getAllUsersNoPagination();
                
                // Import getAllTeams dynamically
                const { getAllTeams } = await import('../../../services/teamService');
                const teamsPromise = getAllTeams();
                
                const [projectsData, usersData, teamsData] = await Promise.all([
                    projectsPromise,
                    usersPromise,
                    teamsPromise
                ]);

                // Store teams for later use in filtering
                setTeams(teamsData);

                // Filter out admin users but keep team leads in reporting views
                let filteredUsers = usersData.filter(u => u.role !== 'admin');
                
                // Filter projects and users based on current user's role
                let filteredProjects = [...projectsData];

                // Filter users and projects based on current user's role
                if (user) {
                    console.log('User role:', user.role);
                    console.log('User ID:', user._id || user.id); // Use user._id as primary, fallback to user.id
                    console.log('Full user object:', user);
                    
                    // Get the user ID consistently
                    const currentUserId = user._id || user.id;
                    
                    // For regular users (role 'user'), automatically set their userId and use assigned projects
                    if (user.role === 'user') {
                        console.log('User is a regular user - setting userId automatically');
                        setUserId(currentUserId);
                        
                        // For regular users, create a user array with just themselves
                        filteredUsers = [{
                            _id: currentUserId,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            username: user.username,
                            role: user.role
                        }];
                        
                        // For regular users, projects are already filtered by the backend API
                        filteredProjects = projectsData;
                        console.log('Regular user assigned projects:', filteredProjects.length);
                    } else if (user.role === 'team_lead') {
                        console.log('User is a team lead');
                        
                        // For debugging, log the teams structure
                        console.log('User teams:', user.teams);
                        
                        // Check if user has teamMembers directly
                        if (user.teamMembers && user.teamMembers.length > 0) {
                            console.log('Using teamMembers array directly:', user.teamMembers);
                            
                            // Filter users to only show the team lead and their team members
                            filteredUsers = filteredUsers.filter(u => 
                                String(u._id) === String(currentUserId) || // Self
                                user.teamMembers.some(id => String(id) === String(u._id)) // Team members
                            );
                            
                            // For projects, use teams array if available
                            if (user.teams && user.teams.length > 0) {
                                // Get only the teams where the user is the team lead
                                const leadTeams = user.teams.filter(team => 
                                    String(team.teamLead) === String(currentUserId)
                                );
                                
                                if (leadTeams.length > 0) {
                                    console.log('Found lead teams in teams array:', leadTeams);
                                    
                                    // Get all projects from these teams
                                    const teamProjects = leadTeams.flatMap(team => team.projects || []);
                                    console.log('Team projects from teams array:', teamProjects);
                                    
                                    // Make sure we don't have duplicates
                                    const uniqueTeamProjects = [...new Set(teamProjects.map(id => String(id)))];
                                    
                                    // Filter projects to only show those assigned to the teams where the user is the team lead
                                    filteredProjects = projectsData.filter(project => 
                                        uniqueTeamProjects.includes(String(project._id))
                                    );
                                    
                                    console.log('Filtered projects from teams array:', filteredProjects.map(p => p.name));
                                }
                            }
                            // If no projects found yet, try using ledTeams
                            if (filteredProjects.length === 0 && user.ledTeams && user.ledTeams.length > 0) {
                                console.log('Using ledTeams:', user.ledTeams);
                                
                                // Find the corresponding full teams for these ledTeams
                                const ledTeamIds = user.ledTeams.map(team => team._id);
                                console.log('Led team IDs:', ledTeamIds);
                                
                                // Find the full team objects from the user.teams array
                                const fullLedTeams = user.teams ? user.teams.filter(team => 
                                    ledTeamIds.some(id => String(id) === String(team._id))
                                ) : [];
                                
                                console.log('Full led teams:', fullLedTeams);
                                
                                // Get all projects from these teams
                                const teamProjects = fullLedTeams.flatMap(team => team.projects || []);
                                console.log('Team projects from led teams:', teamProjects);
                                
                                // Make sure we don't have duplicates
                                const uniqueTeamProjects = [...new Set(teamProjects.map(id => String(id)))];
                                
                                // Filter projects to only show those assigned to the teams where the user is the team lead
                                filteredProjects = projectsData.filter(project => 
                                    uniqueTeamProjects.includes(String(project._id))
                                );
                                
                                console.log('Filtered projects from led teams:', filteredProjects.map(p => p.name));
                            }
                        } else if (user.teams && user.teams.length > 0) {
                            // Log each team's teamLead for debugging
                            user.teams.forEach((team, index) => {
                                console.log(`Team ${index} - Name: ${team.name}, TeamLead: ${team.teamLead}, User ID: ${currentUserId}`);
                                console.log(`Types - TeamLead: ${typeof team.teamLead}, User ID: ${typeof currentUserId}`);
                                console.log(`Comparison: ${team.teamLead === currentUserId}`);
                                console.log(`String comparison: ${String(team.teamLead) === String(currentUserId)}`);
                            });
                            
                            // Get only the teams where the user is the team lead
                            // Use string comparison to handle potential type differences
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(currentUserId)
                            );
                            
                            console.log('Teams where user is team lead:', leadTeams);
                            
                            if (leadTeams.length > 0) {
                                // Get all members from these teams
                                const teamMembers = leadTeams.flatMap(team => team.members || []);
                                
                                // Add the team lead to the list of members if not already included
                                if (!teamMembers.includes(currentUserId) && !teamMembers.some(id => String(id) === String(currentUserId))) {
                                    teamMembers.push(currentUserId);
                                }
                                
                                // Make sure we don't have duplicates
                                const uniqueTeamMembers = [...new Set(teamMembers.map(id => String(id)))];
                                
                                console.log('Team members for teams led by user:', uniqueTeamMembers);
                                
                                // Get all projects from these teams
                                const teamProjects = leadTeams.flatMap(team => team.projects || []);
                                console.log('Team projects from teams where user is team lead:', teamProjects);
                                
                                // Make sure we don't have duplicates
                                const uniqueTeamProjects = [...new Set(teamProjects.map(id => String(id)))];
                                
                                console.log('Projects for teams led by user:', uniqueTeamProjects);
                                
                                // Filter users to only show the team lead and their team members
                                filteredUsers = filteredUsers.filter(u => 
                                    uniqueTeamMembers.includes(String(u._id))
                                );
                                
                                // Filter projects to only show those assigned to the teams where the user is the team lead
                                filteredProjects = projectsData.filter(project => 
                                    uniqueTeamProjects.includes(String(project._id))
                                );
                                
                                console.log('Filtered users count:', filteredUsers.length);
                                console.log('Filtered projects count:', filteredProjects.length);
                            } else {
                                console.log('User is not a team lead for any team');
                                
                                // If user is not a team lead for any team, they can only see themselves
                                filteredUsers = filteredUsers.filter(u => String(u._id) === String(currentUserId));
                                
                                // And they don't see any projects
                                filteredProjects = [];
                            }
                        } else {
                            console.log('No teams or teamMembers array found');
                            
                            // If user has no teams or teamMembers, they can only see themselves
                            filteredUsers = filteredUsers.filter(u => String(u._id) === String(currentUserId));
                            
                            // And they don't see any projects
                            filteredProjects = [];
                        }
                    }
                    // Admins and accountants can see all non-admin users and all projects (already filtered above)
                }

                // Sort users by name
                const sortedUsers = filteredUsers.sort((a, b) => {
                    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                });

                // Sort projects by name
                const sortedProjects = filteredProjects.sort((a, b) =>
                    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                );

                console.log('Final sorted users count:', sortedUsers.length);
                console.log('Final sorted projects count:', sortedProjects.length);
                console.log('Final sorted projects:', sortedProjects.map(p => p.name));

                setAllProjectsUnfiltered(sortedProjects); // Store all projects
                setProjects(sortedProjects); // Initially show all projects
                setUsers(sortedUsers);
            } catch (err) {
                console.error('Error loading data:', err);
                setError('Failed to load projects or users');
            }
        };
        loadData();
    }, [user]);

    // Filter projects when user changes
    useEffect(() => {
        if (!userId || !teams.length || !allProjectsUnfiltered.length) {
            // If no user selected, show all projects
            setProjects(allProjectsUnfiltered);
            setProjectIds([]); // Reset project selection
            return;
        }

        try {
            // Find the selected user
            const selectedUser = users.find(u => u._id === userId);

            if (!selectedUser) {
                setProjects(allProjectsUnfiltered);
                return;
            }

            // Method 1: Try to get teams from user's memberTeam field
            let userTeams = [];
            if (selectedUser.memberTeam && Array.isArray(selectedUser.memberTeam) && selectedUser.memberTeam.length > 0) {
                const userTeamIds = selectedUser.memberTeam.map(team => typeof team === 'object' ? team._id : team);
                userTeams = teams.filter(team => userTeamIds.includes(team._id));
            } else if (selectedUser.memberTeam && !Array.isArray(selectedUser.memberTeam)) {
                // Handle case where memberTeam is a single team object/ID instead of array
                const teamId = typeof selectedUser.memberTeam === 'object' ? selectedUser.memberTeam._id : selectedUser.memberTeam;
                userTeams = teams.filter(team => team._id === teamId);
            }

            // Method 2: If no teams found via memberTeam, search teams for this user as a member
            if (userTeams.length === 0) {
                userTeams = teams.filter(team => {
                    if (!team.members || team.members.length === 0) {
                        return false;
                    }
                    
                    // Check if the user is in this team's members array
                    return team.members.some(member => {
                        const memberId = typeof member === 'object' ? member._id : member;
                        return memberId === selectedUser._id;
                    });
                });
            }

            if (userTeams.length === 0) {
                setProjects([]);
                setProjectIds([]); // Reset project selection
                return;
            }

            // Create a map of project IDs to full project objects
            const projectMap = new Map(allProjectsUnfiltered.map(project => [project._id, project]));

            // Collect all projects from all teams
            let allTeamProjects = [];
            userTeams.forEach(team => {
                if (team.projects && Array.isArray(team.projects)) {
                    allTeamProjects = [...allTeamProjects, ...team.projects];
                }
            });

            // Get full project objects and remove duplicates
            const projectIds = new Set();
            const filteredProjects = [];
            
            allTeamProjects.forEach(teamProject => {
                const projectId = typeof teamProject === 'object' ? teamProject._id : teamProject;
                if (!projectIds.has(projectId)) {
                    const project = projectMap.get(projectId);
                    if (project) {
                        projectIds.add(projectId);
                        filteredProjects.push(project);
                    }
                }
            });

            // Sort filtered projects by name before setting state
            const sortedProjects = filteredProjects.sort((a, b) => a.name.localeCompare(b.name));
            setProjects(sortedProjects);
            setProjectIds([]); // Reset project selection when user changes
        } catch (err) {
            console.error('Error filtering projects:', err);
            setError('Failed to filter projects');
        }
    }, [userId, users, teams, allProjectsUnfiltered]);

    const handleSearch = async () => {
        try {
            setError('');
            setDateError('');
            
            // Basic date validation
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                return;
            }

            // Validate date format
            if (!isValidDateFormat(startDate) || !isValidDateFormat(endDate)) {
                setDateError('Invalid date format. Please use MM/DD/YYYY');
                setError('Please correct the date format (MM/DD/YYYY)');
                return;
            }

            // Parse dates from MM/DD/YYYY format
            const [startMonth, startDay, startYear] = startDate.split('/');
            const [endMonth, endDay, endYear] = endDate.split('/');

            // Format dates as YYYY-MM-DD for API
            const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
            const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;

            console.log('Search parameters:', {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                projectIds,
                projectIdsType: typeof projectIds,
                userId,
                status
            });
            
            const data = await customSearch(formattedStartDate, formattedEndDate, projectIds, userId, status);
            console.log('Search results:', data);
            setResults(data);
        } catch (err) {
            console.error('Search error:', err);
            setError(err.message || 'Failed to perform search');
        }
    };

    const handleExport = async () => {
        try {
            setError('');
            setDateError('');
            
            // Basic date validation
            if (!startDate || !endDate) {
                setError('Please select both start and end dates');
                return;
            }

            // Validate date format
            if (!isValidDateFormat(startDate) || !isValidDateFormat(endDate)) {
                setDateError('Invalid date format. Please use MM/DD/YYYY');
                setError('Please correct the date format (MM/DD/YYYY)');
                return;
            }

            // Use sorted results if available, otherwise use original results
            const dataToExport = sortedResults.length > 0 ? sortedResults : results.filter(row => row.username);
            
            if (dataToExport.length === 0) {
                setError('No data to export');
                return;
            }

            // Helper function to split date range into start and end dates
            const splitDateRange = (dateRangeString) => {
                if (!dateRangeString) return { startDate: '', endDate: '' };
                
                // Split the date range string (e.g., "2025-05-04 to 2025-05-10")
                const parts = dateRangeString.split(' to ');
                if (parts.length !== 2) return { startDate: dateRangeString, endDate: '' };
                
                const formatSingleDate = (dateStr) => {
                    const [year, month, day] = dateStr.split('-');
                    const shortYear = year.slice(-2); // Get last 2 digits of year
                    return `${month}/${day}/${shortYear}`;
                };
                
                return {
                    startDate: formatSingleDate(parts[0]),
                    endDate: formatSingleDate(parts[1])
                };
            };

            // Generate CSV content from sorted frontend data
            const csvRows = [];
            
            // Add header row with new column order: Start Date, End Date, Contract/Project, Employee, Su, M, T, W, Th, F, Sa, Total
            csvRows.push('Start Date,End Date,Contract/Project,Employee,Su,M,T,W,Th,F,Sa,Total');
            
            // Add data rows (excluding row numbers)
            dataToExport.forEach(row => {
                const { startDate: rowStartDate, endDate: rowEndDate } = splitDateRange(row.dateRange);
                const dataRow = [
                    rowStartDate || '',
                    rowEndDate || '',
                    row.project || '',
                    row.username || '',
                    row.sunday || 0,
                    row.monday || 0,
                    row.tuesday || 0,
                    row.wednesday || 0,
                    row.thursday || 0,
                    row.friday || 0,
                    row.saturday || 0,
                    row.hours || 0
                ];
                csvRows.push(dataRow.join(','));
                
                // Add comment row if there are comments
                const hasComments = row.sundayComments || row.mondayComments || row.tuesdayComments || 
                                  row.wednesdayComments || row.thursdayComments || row.fridayComments || 
                                  row.saturdayComments;
                
                if (hasComments) {
                    const commentRow = [
                        '', // Empty start date
                        '', // Empty end date
                        '', // Empty project
                        '', // Empty employee
                        row.sundayComments || '',
                        row.mondayComments || '',
                        row.tuesdayComments || '',
                        row.wednesdayComments || '',
                        row.thursdayComments || '',
                        row.fridayComments || '',
                        row.saturdayComments || '',
                        '' // Empty total
                    ];
                    csvRows.push(commentRow.join(','));
                }
            });
            
            // Create CSV content
            const csvContent = csvRows.join('\n');
            
            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            // Format dates for filename
            const formatDateForFilename = (dateStr) => {
                const [month, day, year] = dateStr.split('/');
                return `${month}-${day}-${year}`;
            };
            
            const formattedStartDate = formatDateForFilename(startDate);
            const formattedEndDate = formatDateForFilename(endDate);
            a.download = `custom-search-${formattedStartDate}_to_${formattedEndDate}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            
        } catch (err) {
            console.error('Export error:', err);
            setError(err.message || 'Failed to export data');
        }
    };

    const handleCalendarClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setCalendarType(type);
    };

    const handleCalendarClose = () => {
        setAnchorEl(null);
        setCalendarType(null);
    };

    const handleDateSelect = (date) => {
        const selectedDate = dayjs(date);
        const formattedDate = selectedDate.format('MM/DD/YYYY');
        
        if (calendarType === 'start') {
            setStartDate(formattedDate);
        } else {
            setEndDate(formattedDate);
        }
        handleCalendarClose();
    };

    // Handle project selection change for multi-select
    const handleProjectChange = (event) => {
        const value = event.target.value;
        setProjectIds(typeof value === 'string' ? value.split(',') : value);
    };

    // Render selected project names
    const renderProjectValue = (selected) => {
        if (selected.length === 0) {
            return 'All Projects';
        }
        if (selected.length === 1) {
            const project = projects.find(p => p._id === selected[0]);
            return project ? project.name : '';
        }
        return `${selected.length} projects selected`;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 3,
                '@media (min-width:601px) and (max-width:1199px)': {
                    p: 2, // Moderate padding for tablets
                    paddingTop: 1, // Reduced top padding for tablets
                    paddingBottom: 2, // Normal bottom padding for tablets
                },
                '@media (max-width:600px)': {
                    p: 1, // Much more reduced padding for mobile
                    paddingTop: 0, // No top padding - layout provides spacing
                    paddingBottom: '88px', // Account for bottom navigation
                },
                '@media (max-width:600px) and (orientation: landscape)': {
                    p: 0.5, // Minimal padding in landscape mode
                    paddingTop: 0, // No top padding
                    paddingBottom: 0.5, // Minimal bottom padding
                    height: 'auto', // Allow natural height
                    overflow: 'visible', // Allow content to overflow
                    minHeight: 'auto', // Remove height constraints
                }
            }}>
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (min-width:601px) and (max-width:1199px)': {
                    mb: 2, // Moderate margin for tablets
                },
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:600px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">Custom Search</Typography>
            </Box>

            {/* Search Form */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                {/* Desktop Layout */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:1199px)': {
                        display: 'none', // Hide desktop layout on tablets and mobile
                    }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Start Date */}
                        <DateInput 
                            label="Start Date" 
                            value={startDate} 
                            onChange={setStartDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            error={Boolean(dateError)}
                        />

                        {/* End Date */}
                        <DateInput 
                            label="End Date" 
                            value={endDate} 
                            onChange={setEndDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            error={Boolean(dateError)}
                        />

                        <Divider orientation="vertical" flexItem />

                        {/* Users Dropdown - Only show for admin, accountant, and team_lead roles */}
                        {user && (user.role === 'admin' || user.role === 'accountant' || user.role === 'team_lead') && (
                            <FormControl sx={{ minWidth: 150, maxWidth: 180 }} size="small">
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Projects Dropdown */}
                        <FormControl sx={{ minWidth: 150, maxWidth: 180 }} size="small">
                            <InputLabel>Projects</InputLabel>
                            <Select
                                multiple
                                value={projectIds}
                                onChange={handleProjectChange}
                                renderValue={renderProjectValue}
                                label="Projects"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                py: 0.25,
                                                minHeight: 'auto'
                                            }
                                        }
                                    }
                                }}
                            >
                                {projects.map((project) => (
                                    <MenuItem key={project._id} value={project._id}>
                                        <Checkbox checked={projectIds.indexOf(project._id) > -1} sx={{ py: 0.5 }} />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Dropdown */}
                        <FormControl sx={{ minWidth: 150, maxWidth: 180 }} size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Certified">Certified</MenuItem>
                                <MenuItem value="Partially Approved">Partially Approved</MenuItem>
                                <MenuItem value="Partially Certified">Partially Certified</MenuItem>
                                <MenuItem value="Timecard Not Created">Timecard Not Created</MenuItem>
                                <MenuItem value="Uncertified">Uncertified</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                            <Button 
                                variant="contained" 
                                onClick={handleSearch}
                            >
                                Find
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setProjectIds([]);
                                    // Only clear userId if user is not a regular user (since regular users have it auto-set)
                                    if (user && user.role !== 'user') {
                                        setUserId('');
                                    }
                                    setStatus('');
                                    setResults([]);
                                    setError('');
                                    setDateError('');
                                }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        disabled={results.length === 0}
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                </Box>

                {/* Tablet Portrait Layout */}
                <Box sx={{ 
                    display: 'none',
                    '@media (min-width:601px) and (max-width:1199px) and (orientation: portrait)': {
                        display: 'block',
                        px: 2, // Add horizontal padding
                        py: 2, // Add vertical padding
                    }
                }}>
                    {/* Date Fields Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 2,
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <DateInput 
                            label="Start Date" 
                            value={startDate} 
                            onChange={setStartDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            error={Boolean(dateError)}
                        />
                        <DateInput 
                            label="End Date" 
                            value={endDate} 
                            onChange={setEndDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            error={Boolean(dateError)}
                        />
                    </Box>

                    {/* Dropdowns Column - Stack all 3 dropdowns vertically */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 2, 
                        mb: 2
                    }}>
                        {/* User Dropdown - Only show for admin, accountant, and team_lead roles */}
                        {user && (user.role === 'admin' || user.role === 'accountant' || user.role === 'team_lead') && (
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Project Dropdown */}
                        <FormControl size="small" sx={{ width: '100%' }}>
                            <InputLabel>Projects</InputLabel>
                            <Select
                                multiple
                                value={projectIds}
                                onChange={handleProjectChange}
                                renderValue={renderProjectValue}
                                label="Projects"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                py: 0.25,
                                                minHeight: 'auto'
                                            }
                                        }
                                    }
                                }}
                            >
                                {projects.map((project) => (
                                    <MenuItem key={project._id} value={project._id}>
                                        <Checkbox checked={projectIds.indexOf(project._id) > -1} sx={{ py: 0.5 }} />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Dropdown */}
                        <FormControl size="small" sx={{ width: '100%' }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Certified">Certified</MenuItem>
                                <MenuItem value="Partially Approved">Partially Approved</MenuItem>
                                <MenuItem value="Partially Certified">Partially Certified</MenuItem>
                                <MenuItem value="Timecard Not Created">Timecard Not Created</MenuItem>
                                <MenuItem value="Uncertified">Uncertified</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Buttons Row - All 3 buttons together */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2,
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Button 
                            variant="contained" 
                            onClick={handleSearch}
                            size="medium"
                            sx={{ minWidth: 100 }}
                        >
                            Find
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setProjectIds([]);
                                // Only clear userId if user is not a regular user (since regular users have it auto-set)
                                if (user && user.role !== 'user') {
                                    setUserId('');
                                }
                                setStatus('');
                                setResults([]);
                                setError('');
                                setDateError('');
                            }}
                            size="medium"
                            sx={{ minWidth: 100 }}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            disabled={results.length === 0}
                            onClick={handleExport}
                            size="medium"
                            sx={{ minWidth: 100 }}
                        >
                            Export
                        </Button>
                    </Box>
                </Box>

                {/* Tablet Landscape Layout */}
                <Box sx={{ 
                    display: 'none',
                    '@media (min-width:601px) and (max-width:1199px) and (orientation: landscape)': {
                        display: 'block',
                        px: 2, // Add horizontal padding
                        py: 1.5, // Add vertical padding
                    }
                }}>
                    {/* Date Fields Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 2,
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <DateInput 
                            label="Start Date" 
                            value={startDate} 
                            onChange={setStartDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            error={Boolean(dateError)}
                        />
                        <DateInput 
                            label="End Date" 
                            value={endDate} 
                            onChange={setEndDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            error={Boolean(dateError)}
                        />
                    </Box>

                    {/* Dropdowns and Buttons Row - All in one row for landscape */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1.5, 
                        mb: 1.5,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        {/* User Dropdown - Only show for admin, accountant, and team_lead roles */}
                        {user && (user.role === 'admin' || user.role === 'accountant' || user.role === 'team_lead') && (
                            <FormControl size="small" sx={{ minWidth: 140, maxWidth: 160 }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Project Dropdown */}
                        <FormControl size="small" sx={{ minWidth: 140, maxWidth: 160 }}>
                            <InputLabel>Projects</InputLabel>
                            <Select
                                multiple
                                value={projectIds}
                                onChange={handleProjectChange}
                                renderValue={renderProjectValue}
                                label="Projects"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                py: 0.25,
                                                minHeight: 'auto'
                                            }
                                        }
                                    }
                                }}
                            >
                                {projects.map((project) => (
                                    <MenuItem key={project._id} value={project._id}>
                                        <Checkbox checked={projectIds.indexOf(project._id) > -1} sx={{ py: 0.5 }} />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Dropdown */}
                        <FormControl size="small" sx={{ minWidth: 140, maxWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Certified">Certified</MenuItem>
                                <MenuItem value="Partially Approved">Partially Approved</MenuItem>
                                <MenuItem value="Partially Certified">Partially Certified</MenuItem>
                                <MenuItem value="Timecard Not Created">Timecard Not Created</MenuItem>
                                <MenuItem value="Uncertified">Uncertified</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Action Buttons */}
                        <Button 
                            variant="contained" 
                            onClick={handleSearch}
                            size="small"
                            sx={{ minWidth: 80 }}
                        >
                            Find
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setProjectIds([]);
                                // Only clear userId if user is not a regular user (since regular users have it auto-set)
                                if (user && user.role !== 'user') {
                                    setUserId('');
                                }
                                setStatus('');
                                setResults([]);
                                setError('');
                                setDateError('');
                            }}
                            size="small"
                            sx={{ minWidth: 80 }}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            disabled={results.length === 0}
                            onClick={handleExport}
                            size="small"
                            sx={{ minWidth: 80 }}
                        >
                            Export
                        </Button>
                    </Box>
                </Box>

                {/* Mobile Portrait Layout */}
                <Box sx={{ 
                    display: 'none',
                    '@media (max-width:600px) and (orientation: portrait)': {
                        display: 'block',
                        px: 1, // Add horizontal padding
                        py: 1.5, // Add vertical padding
                    }
                }}>
                    {/* Date Fields Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        mb: 2,
                        '@media (max-width:600px)': {
                            flexDirection: 'row', // Keep dates side by side
                            justifyContent: 'space-between',
                        }
                    }}>
                        <Box sx={{ flex: 1, mr: 0.5 }}>
                            <DateInput 
                                label="Start Date" 
                                value={startDate} 
                                onChange={setStartDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                                error={Boolean(dateError)}
                            />
                        </Box>
                        <Box sx={{ flex: 1, ml: 0.5 }}>
                            <DateInput 
                                label="End Date" 
                                value={endDate} 
                                onChange={setEndDate}
                                onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                                error={Boolean(dateError)}
                            />
                        </Box>
                    </Box>

                    {/* Dropdowns Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1.5, 
                        mb: 2
                    }}>
                        {/* User Dropdown - Only show for admin, accountant, and team_lead roles */}
                        {user && (user.role === 'admin' || user.role === 'accountant' || user.role === 'team_lead') && (
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Project Dropdown */}
                        <FormControl size="small" sx={{ width: '100%' }}>
                            <InputLabel>Projects</InputLabel>
                            <Select
                                multiple
                                value={projectIds}
                                onChange={handleProjectChange}
                                renderValue={renderProjectValue}
                                label="Projects"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                py: 0.25,
                                                minHeight: 'auto'
                                            }
                                        }
                                    }
                                }}
                            >
                                {projects.map((project) => (
                                    <MenuItem key={project._id} value={project._id}>
                                        <Checkbox checked={projectIds.indexOf(project._id) > -1} sx={{ py: 0.5 }} />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Dropdown */}
                        <FormControl size="small" sx={{ width: '100%' }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Certified">Certified</MenuItem>
                                <MenuItem value="Partially Approved">Partially Approved</MenuItem>
                                <MenuItem value="Partially Certified">Partially Certified</MenuItem>
                                <MenuItem value="Timecard Not Created">Timecard Not Created</MenuItem>
                                <MenuItem value="Uncertified">Uncertified</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Buttons Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                            <Button 
                                variant="contained" 
                                onClick={handleSearch}
                                size="small"
                                sx={{ flex: 1 }}
                            >
                                Find
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setProjectIds([]);
                                    // Only clear userId if user is not a regular user (since regular users have it auto-set)
                                    if (user && user.role !== 'user') {
                                        setUserId('');
                                    }
                                    setStatus('');
                                    setResults([]);
                                    setError('');
                                    setDateError('');
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                            >
                                Clear
                            </Button>
                        </Box>
                        
                        {/* Export Button - Icon Only for Mobile */}
                        <Button
                            variant="outlined"
                            disabled={results.length === 0}
                            onClick={handleExport}
                            size="small"
                            sx={{
                                minWidth: 'auto',
                                padding: '6px',
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1rem',
                                }
                            }}
                        >
                            <FileDownloadIcon />
                        </Button>
                    </Box>
                </Box>

                {/* Mobile Landscape Layout */}
                <Box sx={{ 
                    display: 'none',
                    '@media (max-width:600px) and (orientation: landscape)': {
                        display: 'block',
                        px: 1, // Add horizontal padding
                        py: 1, // Add vertical padding
                    }
                }}>
                    {/* Date Fields Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 1.5,
                        justifyContent: 'center', // Center the date fields
                        alignItems: 'center',
                    }}>
                        <DateInput 
                            label="Start Date" 
                            value={startDate} 
                            onChange={setStartDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'start')}
                            error={Boolean(dateError)}
                        />
                        <DateInput 
                            label="End Date" 
                            value={endDate} 
                            onChange={setEndDate}
                            onCalendarClick={(e) => handleCalendarClick(e, 'end')}
                            error={Boolean(dateError)}
                        />
                    </Box>

                    {/* Dropdowns Row - 3 dropdowns side by side */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        mb: 1.5
                    }}>
                        {/* User Dropdown - Only show for admin, accountant, and team_lead roles */}
                        {user && (user.role === 'admin' || user.role === 'accountant' || user.role === 'team_lead') && (
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    label="User"
                                >
                                    <MenuItem value="">All Users</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user._id} value={user._id}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Project Dropdown */}
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Projects</InputLabel>
                            <Select
                                multiple
                                value={projectIds}
                                onChange={handleProjectChange}
                                renderValue={renderProjectValue}
                                label="Projects"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                py: 0.25,
                                                minHeight: 'auto'
                                            }
                                        }
                                    }
                                }}
                            >
                                {projects.map((project) => (
                                    <MenuItem key={project._id} value={project._id}>
                                        <Checkbox checked={projectIds.indexOf(project._id) > -1} sx={{ py: 0.5 }} />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Dropdown */}
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Certified">Certified</MenuItem>
                                <MenuItem value="Partially Approved">Partially Approved</MenuItem>
                                <MenuItem value="Partially Certified">Partially Certified</MenuItem>
                                <MenuItem value="Timecard Not Created">Timecard Not Created</MenuItem>
                                <MenuItem value="Uncertified">Uncertified</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Buttons Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                            <Button 
                                variant="contained" 
                                onClick={handleSearch}
                                size="small"
                                sx={{ flex: 1 }}
                            >
                                Find
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setProjectIds([]);
                                    // Only clear userId if user is not a regular user (since regular users have it auto-set)
                                    if (user && user.role !== 'user') {
                                        setUserId('');
                                    }
                                    setStatus('');
                                    setResults([]);
                                    setError('');
                                    setDateError('');
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                            >
                                Clear
                            </Button>
                        </Box>
                        
                        {/* Export Button - Icon Only for Mobile Landscape */}
                        <Button
                            variant="outlined"
                            disabled={results.length === 0}
                            onClick={handleExport}
                            size="small"
                            sx={{
                                minWidth: 'auto',
                                padding: '6px',
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1rem',
                                }
                            }}
                        >
                            <FileDownloadIcon />
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {results.length > 0 && (
                <TableContainer component={Paper}>
                    <Table sx={{ userSelect: 'text' }}>
                        <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                            <TableRow>
                                <TableCell sx={{ width: '4%' }} align="center">#</TableCell>
                                <TableCell sx={{ width: '8%' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'username'}
                                        direction={sortColumn === 'username' ? sortOrder : 'asc'}
                                        onClick={() => handleColumnSort('username')}
                                    >
                                        Username
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '12%' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'user'}
                                        direction={sortColumn === 'user' ? sortOrder : 'asc'}
                                        onClick={() => handleColumnSort('user')}
                                    >
                                        User
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '13%' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'project'}
                                        direction={sortColumn === 'project' ? sortOrder : 'asc'}
                                        onClick={() => handleColumnSort('project')}
                                    >
                                        Project
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '13%' }}>Date Range</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Sun</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Mon</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Tue</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Wed</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Thu</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Fri</TableCell>
                                <TableCell sx={{ width: '6%' }} align="center">Sat</TableCell>
                                <TableCell sx={{ width: '8%' }} align="center">Total</TableCell>
                                <TableCell sx={{ width: '10%' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'status'}
                                        direction={sortColumn === 'status' ? sortOrder : 'asc'}
                                        onClick={() => handleColumnSort('status')}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(sortedResults.length > 0 ? sortedResults : results.filter(row => row.username)).map((row, index) => (
                                <React.Fragment key={row.id}>
                                    <TableRow>
                                        <TableCell align="center">{row.username ? index + 1 : ''}</TableCell>
                                        <TableCell>{row.username}</TableCell>
                                        <TableCell>{row.user}</TableCell>
                                        <TableCell>{row.project}</TableCell>
                                        <TableCell>{formatDateRange(row.dateRange)}</TableCell>
                                        <TableCell align="center">{row.sunday ? parseFloat(row.sunday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.monday ? parseFloat(row.monday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.tuesday ? parseFloat(row.tuesday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.wednesday ? parseFloat(row.wednesday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.thursday ? parseFloat(row.thursday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.friday ? parseFloat(row.friday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.saturday ? parseFloat(row.saturday).toFixed(2) : '0.00'}</TableCell>
                                        <TableCell align="center">{row.hours}</TableCell>
                                        <TableCell>{row.status}</TableCell>
                                    </TableRow>
                                    {/* Comments row - only show if there are any comments */}
                                    {(row.sundayComments || row.mondayComments || row.tuesdayComments || 
                                      row.wednesdayComments || row.thursdayComments || row.fridayComments || 
                                      row.saturdayComments) && (
                                        <TableRow>
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.sundayComments || ''}
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.mondayComments || ''}
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.tuesdayComments || ''}
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.wednesdayComments || ''}
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.thursdayComments || ''}
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.fridayComments || ''}
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {row.saturdayComments || ''}
                                            </TableCell>
                                            <TableCell />
                                            <TableCell />
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                            
                            {/* Total row */}
                            {results.length > 0 && (
                                <TableRow sx={{ bgcolor: '#f8f8d7' }}>
                                    <TableCell />
                                    <TableCell>Total</TableCell>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell />
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.sunday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.monday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.tuesday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.wednesday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.thursday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.friday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.saturday || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {results.reduce((sum, row) => sum + parseFloat(row.hours || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleCalendarClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <DateCalendar 
                    onChange={handleDateSelect}
                    showDaysOutsideCurrentMonth
                    firstDayOfWeek={0} // 0 = Sunday
                />
            </Popover>
        </Box>
        </LocalizationProvider>
    );
};

export default CustomSearch;
